import React from 'react';
import {
    Box,
    Text,
    Stack,
    Group,
    Button,
    TextInput,
    Tooltip,
    Alert,
    Loader,
    ActionIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCopy, IconEye, IconEyeOff, IconLockOpen } from '@tabler/icons-react';
import { getEntityValueFromARef } from '../../types';
import { getFieldTooltipTitle } from './entityFieldDebugUtils';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { decryptErrorMessage, fetchDecryptedAttributeValue } from './encryptedAttributeUtils';

export type DecryptRevealState = {
    fieldLabel: string;
    attributePath: string;
    value: string | null;
    loading: boolean;
    error: string | null;
} | null;

export interface EncryptedAttributeFieldProps {
    mode: 'view' | 'edit' | 'create';
    name: string;
    label: React.ReactNode;
    displayValue: unknown;
    fieldLabel: React.ReactNode;
    attributePath: string;
    entityId: string | null | undefined;
    attr: { type?: string; [key: string]: unknown };
    isFieldDisabled: boolean;
    fieldRequired: boolean;
    isReadOnlyField: boolean;
    tier3DecryptEntitled: boolean;
    showTier3DecryptButton: boolean;
    formData: Record<string, unknown>;
    originalData: Record<string, unknown> | null;
    onFieldChange: (name: string, value: unknown) => void;
    setOriginalData: React.Dispatch<React.SetStateAction<Record<string, unknown> | null>>;
    decryptReveal: DecryptRevealState;
    setDecryptReveal: React.Dispatch<React.SetStateAction<DecryptRevealState>>;
    revealedEncryptedFieldNames: string[];
    setRevealedEncryptedFieldNames: React.Dispatch<React.SetStateAction<string[]>>;
    encryptedFieldsClearOnSave: string[];
    setEncryptedFieldsClearOnSave: React.Dispatch<React.SetStateAction<string[]>>;
    encryptedCreateVisible: Record<string, boolean>;
    setEncryptedCreateVisible: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    encryptedEditStoredVisible: Record<string, boolean>;
    setEncryptedEditStoredVisible: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    encryptedViewDecryptedByName: Record<string, string>;
    setEncryptedViewDecryptedByName: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    encryptedViewPlainVisible: Record<string, boolean>;
    setEncryptedViewPlainVisible: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    revealedDecryptedCacheRef: React.MutableRefObject<Record<string, string>>;
    /** Create mode: binding validation UI */
    bindingRegex?: string;
    bindingRegexError?: React.ReactNode;
    bindingDescription?: string;
}

/** Encrypted attribute UI: view-surface decrypt, edit password + decrypt, create password toggle. */
export const EncryptedAttributeField: React.FC<EncryptedAttributeFieldProps> = (props) => {
    const {
        mode,
        name,
        label,
        displayValue,
        fieldLabel,
        attributePath,
        entityId,
        attr,
        isFieldDisabled,
        fieldRequired,
        isReadOnlyField,
        tier3DecryptEntitled,
        showTier3DecryptButton,
        formData,
        originalData,
        onFieldChange,
        setOriginalData,
        decryptReveal,
        setDecryptReveal,
        revealedEncryptedFieldNames,
        setRevealedEncryptedFieldNames,
        encryptedFieldsClearOnSave,
        setEncryptedFieldsClearOnSave,
        encryptedCreateVisible,
        setEncryptedCreateVisible,
        encryptedEditStoredVisible,
        setEncryptedEditStoredVisible,
        encryptedViewDecryptedByName,
        setEncryptedViewDecryptedByName,
        encryptedViewPlainVisible,
        setEncryptedViewPlainVisible,
        revealedDecryptedCacheRef,
        bindingRegex,
        bindingRegexError,
        bindingDescription,
    } = props;

    if (mode === 'create') {
        const showPlain = encryptedCreateVisible[name] === true;
        return (
            <Tooltip label={getFieldTooltipTitle(attr)}>
                <Stack gap="xs" mb="md">
                    {fieldLabel}
                    <Group gap="sm" wrap="nowrap" align="flex-end">
                        <Box flex={1} miw={0}>
                            <TextInput
                                key={name}
                                type={showPlain ? 'text' : 'password'}
                            autoComplete="new-password"
                            value={
                                typeof displayValue === 'string' || typeof displayValue === 'number'
                                    ? displayValue
                                    : String(displayValue ?? '')
                            }
                            onChange={(e) => onFieldChange(name, e.currentTarget.value)}
                            disabled={isFieldDisabled}
                            required={fieldRequired}
                            withAsterisk={false}
                            error={bindingRegexError}
                            description={bindingDescription}
                            aria-label={typeof label === 'string' ? label : name}
                        />
                        </Box>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="lg"
                            flex="0 0 auto"
                            mb={2}
                            disabled={isFieldDisabled}
                            onClick={() => setEncryptedCreateVisible((prev) => ({ ...prev, [name]: !prev[name] }))}
                            aria-label={showPlain ? 'Hide password' : 'Show password'}
                        >
                            {showPlain ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                        </ActionIcon>
                    </Group>
                </Stack>
            </Tooltip>
        );
    }

    if (mode === 'view') {
        const showStored = encryptedEditStoredVisible[name] === true;
        const storedStr =
            typeof displayValue === 'string' || typeof displayValue === 'number'
                ? String(displayValue ?? '')
                : displayValue != null
                  ? JSON.stringify(displayValue)
                  : '';

        const fromDecryptCache = revealedDecryptedCacheRef.current[attributePath];
        const decryptedPlain =
            encryptedViewDecryptedByName[name] ?? (fromDecryptCache !== undefined ? fromDecryptCache : undefined);
        const hasDecrypted =
            Object.prototype.hasOwnProperty.call(encryptedViewDecryptedByName, name) ||
            fromDecryptCache !== undefined;
        const plainVisible = encryptedViewPlainVisible[name] === true;

        const isDecryptPath = decryptReveal?.attributePath === attributePath;
        const isDecryptLoading = isDecryptPath && !!decryptReveal?.loading;
        const isDecryptError = isDecryptPath && !!decryptReveal?.error;

        const handleDecryptViewSurface = () => {
            if (!entityId || !tier3DecryptEntitled) return;
            if (hasDecrypted) return;
            const cached = revealedDecryptedCacheRef.current[attributePath];
            if (cached !== undefined) {
                setEncryptedViewDecryptedByName((prev) => ({ ...prev, [name]: cached }));
                setEncryptedViewPlainVisible((prev) => ({ ...prev, [name]: false }));
                setDecryptReveal(null);
                return;
            }
            setDecryptReveal({
                fieldLabel: String(label ?? name),
                attributePath,
                value: null,
                loading: true,
                error: null,
            });
            fetchDecryptedAttributeValue(entityId, attributePath)
                .then((str) => {
                    revealedDecryptedCacheRef.current = {
                        ...revealedDecryptedCacheRef.current,
                        [attributePath]: str,
                    };
                    setEncryptedViewDecryptedByName((prev) => ({ ...prev, [name]: str }));
                    setEncryptedViewPlainVisible((prev) => ({ ...prev, [name]: false }));
                    setDecryptReveal(null);
                })
                .catch((err: unknown) => {
                    setDecryptReveal((prev) =>
                        prev ? { ...prev, value: null, loading: false, error: decryptErrorMessage(err) } : null
                    );
                });
        };

        const handleCopyDecryptedViewSurface = async () => {
            if (!hasDecrypted) return;
            const str = decryptedPlain ?? '';
            const ok = await copyToClipboard(str);
            notifications.show({
                title: ok ? 'Copied' : 'Copy failed',
                message: ok ? 'Decrypted value copied to clipboard.' : 'Could not copy to clipboard.',
                color: ok ? 'green' : 'red',
            });
        };

        return (
            <Box key={name} mb="md">
                <Tooltip label={getFieldTooltipTitle(attr)}>
                    <Stack gap="xs">
                        {fieldLabel}
                        {hasDecrypted ? (
                            <>
                                <TextInput
                                    readOnly
                                    type={plainVisible ? 'text' : 'password'}
                                    value={decryptedPlain ?? ''}
                                    styles={{ input: { fontFamily: 'monospace' } }}
                                    aria-label={typeof label === 'string' ? label : String(name)}
                                />
                                <Group gap="sm" wrap="wrap">
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        leftSection={<IconCopy size={16} />}
                                        onClick={() => void handleCopyDecryptedViewSurface()}
                                    >
                                        Copy
                                    </Button>
                                    {!plainVisible ? (
                                        <Button
                                            size="xs"
                                            variant="outline"
                                            leftSection={<IconEye size={16} />}
                                            onClick={() =>
                                                setEncryptedViewPlainVisible((prev) => ({ ...prev, [name]: true }))
                                            }
                                        >
                                            Reveal
                                        </Button>
                                    ) : (
                                        <Button
                                            size="xs"
                                            variant="outline"
                                            leftSection={<IconEyeOff size={16} />}
                                            onClick={() =>
                                                setEncryptedViewPlainVisible((prev) => ({ ...prev, [name]: false }))
                                            }
                                        >
                                            Mask
                                        </Button>
                                    )}
                                </Group>
                                <Button
                                    variant="subtle"
                                    size="xs"
                                    onClick={() =>
                                        setEncryptedEditStoredVisible((prev) => ({ ...prev, [name]: !prev[name] }))
                                    }
                                >
                                    {showStored ? 'Hide stored ciphertext' : 'Show stored ciphertext'}
                                </Button>
                                {showStored ? (
                                    <>
                                        <Text size="xs" c="dimmed">
                                            Stored value (encrypted payload)
                                        </Text>
                                        <Text
                                            size="sm"
                                            ff="monospace"
                                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                        >
                                            {storedStr || '—'}
                                        </Text>
                                    </>
                                ) : null}
                            </>
                        ) : (
                            <>
                                {!showStored ? (
                                    <Group gap="sm" wrap="wrap" align="center">
                                        <Text size="sm" ff="monospace">
                                            •••••••••••••
                                        </Text>
                                        <Button
                                            size="xs"
                                            variant="outline"
                                            leftSection={<IconEye size={16} />}
                                            onClick={() =>
                                                setEncryptedEditStoredVisible((prev) => ({ ...prev, [name]: true }))
                                            }
                                        >
                                            Show stored value
                                        </Button>
                                        {showTier3DecryptButton ? (
                                            <Tooltip
                                                label="You do not have permission to decrypt this attribute."
                                                disabled={tier3DecryptEntitled}
                                            >
                                                <Box display="inline-block">
                                                    <Button
                                                        size="xs"
                                                        variant="outline"
                                                        leftSection={
                                                            isDecryptLoading ? (
                                                                <Loader size="xs" />
                                                            ) : (
                                                                <IconLockOpen size={16} />
                                                            )
                                                        }
                                                        disabled={
                                                            !tier3DecryptEntitled || isDecryptLoading || isFieldDisabled
                                                        }
                                                        onClick={handleDecryptViewSurface}
                                                    >
                                                        Decrypt
                                                    </Button>
                                                </Box>
                                            </Tooltip>
                                        ) : null}
                                    </Group>
                                ) : (
                                    <>
                                        <Text size="xs" c="dimmed">
                                            Stored value (encrypted payload)
                                        </Text>
                                        <Text
                                            size="sm"
                                            ff="monospace"
                                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                                        >
                                            {storedStr || '—'}
                                        </Text>
                                        <Group gap="sm" wrap="wrap" align="center">
                                            <Button
                                                size="xs"
                                                variant="outline"
                                                leftSection={<IconEyeOff size={16} />}
                                                onClick={() => {
                                                    setEncryptedEditStoredVisible((prev) => ({ ...prev, [name]: false }));
                                                    if (decryptReveal?.attributePath === attributePath) {
                                                        setDecryptReveal(null);
                                                    }
                                                }}
                                            >
                                                Hide
                                            </Button>
                                            {showTier3DecryptButton ? (
                                                <Tooltip
                                                    label="You do not have permission to decrypt this attribute."
                                                    disabled={tier3DecryptEntitled}
                                                >
                                                    <Box display="inline-block">
                                                        <Button
                                                            size="xs"
                                                            variant="outline"
                                                            leftSection={
                                                                isDecryptLoading ? (
                                                                    <Loader size="xs" />
                                                                ) : (
                                                                    <IconLockOpen size={16} />
                                                                )
                                                            }
                                                            disabled={
                                                                !tier3DecryptEntitled ||
                                                                isDecryptLoading ||
                                                                isFieldDisabled
                                                            }
                                                            onClick={handleDecryptViewSurface}
                                                        >
                                                            Decrypt
                                                        </Button>
                                                    </Box>
                                                </Tooltip>
                                            ) : null}
                                        </Group>
                                    </>
                                )}
                            </>
                        )}
                        {isDecryptLoading ? (
                            <Group gap="xs" align="center" mt="xs">
                                <Loader size="sm" />
                                <Text size="sm" c="dimmed">
                                    Decrypting…
                                </Text>
                            </Group>
                        ) : null}
                        {isDecryptError ? <Alert color="red" mt="xs">{decryptReveal?.error}</Alert> : null}
                    </Stack>
                </Tooltip>
            </Box>
        );
    }

    // mode === 'edit'
    const hasOriginalValue =
        originalData != null &&
        originalData[name] !== undefined &&
        originalData[name] !== null &&
        originalData[name] !== '';
    const isClearOnSave = encryptedFieldsClearOnSave.includes(name);

    if (revealedEncryptedFieldNames.includes(name)) {
        const revealedDisplay =
            typeof displayValue === 'string' || typeof displayValue === 'number'
                ? displayValue
                : String(displayValue ?? '');
        return (
            <Box key={name} mb="md">
                <Tooltip label={isReadOnlyField ? 'This field cannot be edited' : getFieldTooltipTitle(attr)}>
                    <TextInput
                        label={fieldLabel}
                        value={revealedDisplay}
                        onChange={(e) => onFieldChange(name, e.currentTarget.value)}
                        onBlur={(e) => {
                            const v = e.currentTarget.value.trim();
                            if (v !== revealedDisplay) onFieldChange(name, v);
                        }}
                        disabled={isFieldDisabled}
                        required={fieldRequired}
                        withAsterisk={false}
                    />
                </Tooltip>
                {hasOriginalValue && (
                    <Box mt="xs">
                        {isClearOnSave ? (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() =>
                                    setEncryptedFieldsClearOnSave((prev) => prev.filter((n) => n !== name))
                                }
                            >
                                Don't clear on save
                            </Button>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() =>
                                    setEncryptedFieldsClearOnSave((prev) =>
                                        prev.includes(name) ? prev : [...prev, name]
                                    )
                                }
                            >
                                Set to empty on save
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        );
    }

    const isRevealLoading = decryptReveal?.attributePath === attributePath && decryptReveal?.loading;
    const isRevealError = decryptReveal?.attributePath === attributePath && decryptReveal?.error;
    const cipherBaseline = originalData != null ? getEntityValueFromARef(name, originalData) : undefined;
    const formValRaw = getEntityValueFromARef(name, formData);
    const toComparableString = (v: unknown) => {
        if (v === undefined || v === null) return '';
        if (typeof v === 'object') return JSON.stringify(v);
        return String(v);
    };
    const baselineStr = toComparableString(cipherBaseline);
    const formValStr = toComparableString(formValRaw);
    const valuesMatchBaseline = formValStr === baselineStr;
    const editDisplayPassword = valuesMatchBaseline ? '' : formValStr;

    const handleUndecryptedEncryptedPasswordChange = (next: string) => {
        if (next === '') {
            onFieldChange(name, cipherBaseline ?? '');
        } else {
            onFieldChange(name, next);
        }
    };

    const handleDecryptEdit = () => {
        if (!tier3DecryptEntitled || !entityId) return;
        const cached = revealedDecryptedCacheRef.current[attributePath];
        if (cached !== undefined) {
            onFieldChange(name, cached);
            setOriginalData((prev) => (prev ? { ...prev, [name]: cached } : null));
            setRevealedEncryptedFieldNames((prev) => (prev.includes(name) ? prev : [...prev, name]));
            setDecryptReveal(null);
            return;
        }
        setDecryptReveal({
            fieldLabel: String(label ?? name),
            attributePath,
            value: null,
            loading: true,
            error: null,
        });
        fetchDecryptedAttributeValue(entityId, attributePath)
            .then((str) => {
                revealedDecryptedCacheRef.current = {
                    ...revealedDecryptedCacheRef.current,
                    [attributePath]: str,
                };
                onFieldChange(name, str);
                setOriginalData((prev) => (prev ? { ...prev, [name]: str } : null));
                setRevealedEncryptedFieldNames((prev) => (prev.includes(name) ? prev : [...prev, name]));
                setDecryptReveal(null);
            })
            .catch((err: unknown) => {
                setDecryptReveal((prev) =>
                    prev ? { ...prev, value: null, loading: false, error: decryptErrorMessage(err) } : null
                );
            });
    };

    return (
        <Box key={name} mb="md">
            <Tooltip label={isReadOnlyField ? 'This field cannot be edited' : getFieldTooltipTitle(attr)}>
                <Stack gap="xs">
                    {fieldLabel}
                    <Group wrap="nowrap" gap="sm" align="flex-end">
                        <Box flex={1} miw={0}>
                            <TextInput
                                type="password"
                            autoComplete="new-password"
                            value={editDisplayPassword}
                            onChange={(e) => handleUndecryptedEncryptedPasswordChange(e.currentTarget.value)}
                            disabled={isFieldDisabled}
                            required={fieldRequired}
                            withAsterisk={false}
                            placeholder={
                                baselineStr
                                    ? 'Leave empty to keep current password, or type a new one'
                                    : 'Type a new password'
                            }
                            styles={{
                                root: { flex: 1, minWidth: 0 },
                                input: isFieldDisabled ? { opacity: 0.65, cursor: 'not-allowed' } : undefined,
                            }}
                            aria-label={typeof label === 'string' ? label : name}
                        />
                        </Box>
                        <Button
                            size="xs"
                            flex="0 0 auto"
                            mb={2}
                            onClick={handleDecryptEdit}
                            disabled={isFieldDisabled || isRevealLoading || !tier3DecryptEntitled}
                            leftSection={isRevealLoading ? <Loader size="xs" /> : <IconLockOpen size={16} />}
                        >
                            Decrypt
                        </Button>
                    </Group>
                </Stack>
            </Tooltip>
            {isRevealLoading && (
                <Group align="center" gap="xs" mt="xs">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                        Decrypting…
                    </Text>
                </Group>
            )}
            {isRevealError && <Alert color="red" mt="xs">{decryptReveal?.error}</Alert>}
        </Box>
    );
};
